import { ref, inject, onMounted } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface GameElement {
  title: string
  description: string
  iconUrl?: string
  imageUrl?: string
  features: string[]
}

export interface GameElementsState {
  isLoading: Ref<boolean>
  hasError: Ref<boolean>
  title: Ref<string>
  description: Ref<string>
  elements: Ref<GameElement[]>
  fetchGameElementsData: () => Promise<void>
  $t: (key: string, fallback?: string) => string
}

export default function useGameElements(passedContent?: any): GameElementsState {
  const ContentService: any = inject('ContentService')
  const { t, locale } = useI18n()

  const isLoading = ref(true)
  const hasError = ref(false)
  const title = ref('')
  const description = ref('')
  const elements = ref<GameElement[]>([])

  const getMediaUrl = (input: any): string => {
    if (!input) return ''
    if (ContentService?.getMediaUrl) return ContentService.getMediaUrl(input)
    if (typeof input === 'string') return input
    if (input?.url) return input.url
    if (input?.mediaUrl) return input.mediaUrl
    return ''
  }

  const normaliseFeatures = (rawValue: unknown): string[] => {
    if (!rawValue) {
      return []
    }

    if (Array.isArray(rawValue)) {
      return rawValue
        .map(entry => typeof entry === 'string' ? entry.trim() : '')
        .filter(entry => entry.length > 0)
    }

    if (typeof rawValue === 'string') {
      try {
        const parsed = JSON.parse(rawValue)
        if (Array.isArray(parsed)) {
          return parsed
            .map(entry => typeof entry === 'string' ? entry.trim() : '')
            .filter(entry => entry.length > 0)
        }
      } catch (error) {
        // Ignore JSON parse errors and fall back to newline splitting
      }

      return rawValue
        .split(/\r?\n/)
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0)
    }

    return []
  }

  const applyContent = (content: any) => {
    if (!content) return
    
    title.value = content?.title || content?.gameElementsTitle || ''
    description.value = content?.description || content?.gameElementsDescription || ''

    const raw = content?.gameElements || content?.elements || []
    const list: GameElement[] = []

    if (Array.isArray(raw)) {
      for (const item of raw) {
        const features = normaliseFeatures(item?.features)
        const element: GameElement = {
          title: item?.title || '',
          description: item?.description || '',
          iconUrl: getMediaUrl(item?.iconUrl ?? item?.icon),
          imageUrl: getMediaUrl(item?.imageUrl ?? item?.featureImage ?? item?.featureImageUrl),
          features
        }
        list.push(element)
      }
    }

    elements.value = list.filter(item => item.title || item.description || item.imageUrl || item.iconUrl)
  }

  const getPageCulture = (): string | null => {
    if (typeof window === 'undefined') return null
    const pageCulture = (window as any)?.umbracoPageData?.culture
    return typeof pageCulture === 'string' && pageCulture ? pageCulture : null
  }

  const fetchGameElementsData = async () => {
    try {
      // Show passed content immediately (no spinner), then fetch fresh data
      isLoading.value = !passedContent
      hasError.value = false

      if (passedContent) {
        applyContent(passedContent)
      }

      const pageCulture = getPageCulture()
      const culture = pageCulture || (locale.value === 'nl' ? 'nl-nl' : 'en-us')
      
      const homepageData = await ContentService?.getHomepage?.(culture)
      
      if (homepageData) {
        // API returns gameElements at root
        const gameElementsData = {
          title: homepageData?.gameElementsTitle || '',
          description: homepageData?.gameElementsDescription || '',
          gameElements: homepageData?.gameElements || []
        }
        applyContent(gameElementsData)
      }
    } catch (error) {
      console.error('Error fetching game elements data:', error)
      hasError.value = true
      // Leave fields empty; no hardcoded fallbacks
      title.value = ''
      description.value = ''
      elements.value = []
    } finally {
      isLoading.value = false
    }
  }

  // Lifecycle - fetch data on mount
  onMounted(async () => {
    await fetchGameElementsData()
  })

  return {
    isLoading,
    hasError,
    title,
    description,
    elements,
    fetchGameElementsData,
    $t: t
  }
}
